/**
 * MonitorizareDiferenteTab - Tab pentru monitorizarea incidentelor și diferențelor angajaților
 * Funcționalități: vizualizare lunară, rate plată, reportare automată, istoric
 * Permisiuni: monitorizare.vizualizare, monitorizare.adaugare, monitorizare.editare, monitorizare.plata
 */
import { memo, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { 
  AlertTriangle, ChevronDown, ChevronRight, Plus, Search, X, 
  Check, Clock, Archive, Eye, Edit, Trash2, CreditCard, CheckCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { isActiveEmployee } from '../constants';

const API = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${API}/api`; // API cu prefix /api
const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MONTHS_RO = ['', 'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 
  'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];

// Status labels - diferite pentru admin vs dispatcher
const getStatusLabel = (status, canSeePlata) => {
  const labels = {
    'in_analiza': { label: 'În analiză', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'in_plata': { 
      label: canSeePlata ? 'În plată' : 'În reconciliere', 
      color: 'bg-blue-100 text-blue-800', 
      icon: canSeePlata ? CreditCard : RefreshCw 
    },
    'finalizat': { label: 'Finalizat', color: 'bg-green-100 text-green-800', icon: CheckCircle }
  };
  return labels[status] || labels['in_analiza'];
};

const MonitorizareDiferenteTab = memo(function MonitorizareDiferenteTab({
  drivers,
  hasPermission,
  loading
}) {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [incidentsData, setIncidentsData] = useState({ employees: [] });
  const [incidentsLoading, setIncidentsLoading] = useState(false);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  
  // Dialog states
  const [addDialog, setAddDialog] = useState({ open: false });
  const [editDialog, setEditDialog] = useState({ open: false, incident: null });
  const [plataDialog, setPlataDialog] = useState({ open: false, incident: null });
  const [descriereDialog, setDescriereDialog] = useState({ open: false, text: '', title: '' });
  
  // Form states
  const [newIncident, setNewIncident] = useState({
    employee_id: '',
    data_incident: new Date().toISOString().split('T')[0],
    awb: '',
    tip_incident: '',
    valoare: '',
    descriere: ''
  });
  
  const [editIncident, setEditIncident] = useState({
    awb: '',
    tip_incident: '',
    valoare: '',
    descriere: ''
  });
  
  const [planRate, setPlanRate] = useState({
    numar_rate: 1,
    luna_start: now.getMonth() + 1,
    an_start: now.getFullYear()
  });

  // Employees list - use drivers prop or fetch from dedicated endpoint
  const [localEmployees, setLocalEmployees] = useState([]);
  const employeesList = (drivers && drivers.length > 0) ? drivers : localEmployees;

  useEffect(() => {
    // If drivers prop is empty, fetch employees from incidents endpoint
    if (!drivers || drivers.length === 0) {
      const fetchEmployees = async () => {
        try {
          const response = await axios.get(`${API_URL}/incidents/employees-list`, getAuthHeaders());
          setLocalEmployees(response.data || []);
        } catch (err) {
          console.error('Failed to fetch employees for incidents:', err);
        }
      };
      fetchEmployees();
    }
  }, [drivers]);

  // Permisiuni
  const canAdd = hasPermission('monitorizare.adaugare') || hasPermission('setari.editare');
  const canEdit = hasPermission('monitorizare.editare') || hasPermission('setari.editare');
  const canSeePlata = hasPermission('monitorizare.plata') || hasPermission('setari.editare');

  // Fetch incidents
  const fetchIncidents = async () => {
    setIncidentsLoading(true);
    try {
      let statusParam = statusFilter;
      if (statusFilter === 'active') statusParam = '';
      if (statusFilter === 'istoric') statusParam = 'finalizat';
      
      const params = new URLSearchParams({
        year: selectedYear,
        month: selectedMonth,
        ...(statusParam && { status: statusParam })
      });
      
      const response = await axios.get(`${API_URL}/incidents?${params}`, getAuthHeaders());
      setIncidentsData(response.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Eroare la încărcarea incidentelor');
    } finally {
      setIncidentsLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, [selectedMonth, selectedYear, statusFilter]);

  const toggleExpand = (employeeId) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  // Add incident
  const handleAddIncident = async () => {
    if (!newIncident.employee_id || !newIncident.tip_incident || !newIncident.valoare) {
      toast.error('Completează câmpurile obligatorii');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/incidents`, {
        ...newIncident,
        valoare: parseFloat(newIncident.valoare)
      }, getAuthHeaders());
      
      toast.success('Incident adăugat cu succes');
      setAddDialog({ open: false });
      setNewIncident({
        employee_id: '',
        data_incident: new Date().toISOString().split('T')[0],
        awb: '',
        tip_incident: '',
        valoare: '',
        descriere: ''
      });
      fetchIncidents();
    } catch (error) {
      toast.error('Eroare la adăugarea incidentului');
    }
  };

  // Open edit dialog
  const openEditDialog = (incident) => {
    setEditIncident({
      awb: incident.awb || '',
      tip_incident: incident.tip_incident || '',
      valoare: incident.valoare || '',
      descriere: incident.descriere || ''
    });
    setEditDialog({ open: true, incident });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editDialog.incident) return;
    
    try {
      await axios.put(`${API_URL}/incidents/${editDialog.incident.id}`, {
        awb: editIncident.awb,
        tip_incident: editIncident.tip_incident,
        valoare: parseFloat(editIncident.valoare),
        descriere: editIncident.descriere
      }, getAuthHeaders());
      
      toast.success('Incident actualizat');
      setEditDialog({ open: false, incident: null });
      fetchIncidents();
    } catch (error) {
      toast.error('Eroare la actualizare');
    }
  };

  // Pune în plată
  const handlePuneInPlata = async () => {
    if (!plataDialog.incident) return;
    
    try {
      await axios.post(
        `${API_URL}/incidents/${plataDialog.incident.id}/pune-in-plata`,
        planRate,
        getAuthHeaders()
      );
      
      toast.success('Incident pus în plată');
      setPlataDialog({ open: false, incident: null });
      fetchIncidents();
    } catch (error) {
      toast.error('Eroare la punerea în plată');
    }
  };

  // Marchează rata
  const handleMarcheazaRata = async (incident) => {
    try {
      await axios.post(
        `${API_URL}/incidents/${incident.id}/marcheaza-rata`,
        { luna: selectedMonth, an: selectedYear },
        getAuthHeaders()
      );
      
      toast.success('Rată marcată ca plătită');
      fetchIncidents();
    } catch (error) {
      toast.error('Eroare la marcarea ratei');
    }
  };

  // Finalizează
  const handleFinalizeaza = async (incident) => {
    try {
      await axios.post(
        `${API_URL}/incidents/${incident.id}/finalizeaza`,
        {},
        getAuthHeaders()
      );
      
      toast.success('Incident finalizat și mutat în istoric');
      fetchIncidents();
    } catch (error) {
      toast.error('Eroare la finalizare');
    }
  };

  // Delete
  const handleDelete = async (incidentId) => {
    if (!confirm('Sigur dorești să ștergi acest incident?')) return;
    
    try {
      await axios.delete(`${API_URL}/incidents/${incidentId}`, getAuthHeaders());
      toast.success('Incident șters');
      fetchIncidents();
    } catch (error) {
      toast.error('Eroare la ștergere');
    }
  };

  // Filter employees by search
  const filteredEmployees = incidentsData.employees?.filter(emp => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return emp.employee_name?.toLowerCase().includes(query) || 
           emp.username?.toLowerCase().includes(query);
  }) || [];

  // Calculate totals
  const totalRestant = filteredEmployees.reduce((sum, emp) => sum + (emp.total_restant || 0), 0);
  const totalIncidente = filteredEmployees.reduce((sum, emp) => sum + (emp.incidents_count || 0), 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                Monitorizare Diferențe
              </CardTitle>
              <CardDescription>
                Gestionarea incidentelor și diferențelor angajaților
              </CardDescription>
            </div>
            
            {/* Selector perioadă */}
            <div className="flex items-center gap-2">
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS_RO.slice(1).map((month, idx) => (
                    <SelectItem key={idx + 1} value={String(idx + 1)}>{month}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Caută angajat..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="in_analiza">În analiză</SelectItem>
                <SelectItem value="in_plata">{canSeePlata ? 'În plată' : 'În reconciliere'}</SelectItem>
                <SelectItem value="istoric">Istoric</SelectItem>
                <SelectItem value="toate">Toate</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Add button - doar dacă are permisiune */}
            {canAdd && (
              <Button onClick={() => setAddDialog({ open: true })} className="ml-auto">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Incident
              </Button>
            )}
          </div>
          
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <div className="text-xs text-orange-600 font-medium">Total Restant</div>
              <div className="text-xl font-bold text-orange-800">{totalRestant.toLocaleString()} lei</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <div className="text-xs text-blue-600 font-medium">Incidente Active</div>
              <div className="text-xl font-bold text-blue-800">{totalIncidente}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-xs text-slate-600 font-medium">Angajați Afectați</div>
              <div className="text-xl font-bold text-slate-800">{filteredEmployees.length}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="text-xs text-slate-600 font-medium">Perioadă</div>
              <div className="text-xl font-bold text-slate-800">{MONTHS_RO[selectedMonth]} {selectedYear}</div>
            </div>
          </div>
          
          {/* Loading */}
          {incidentsLoading ? (
            <div className="text-center py-8 text-slate-500">Se încarcă...</div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Nu există incidente {statusFilter === 'istoric' ? 'în istoric' : 'active'}</p>
            </div>
          ) : (
            /* Employees list */
            <div className="space-y-2">
              {filteredEmployees.map((emp) => (
                <div key={emp.employee_id} className="border rounded-lg overflow-hidden">
                  {/* Employee header - collapsible */}
                  <div 
                    onClick={() => toggleExpand(emp.employee_id)}
                    className="flex items-center justify-between p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {expandedEmployees[emp.employee_id] ? (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                      <div>
                        <div className="font-semibold text-slate-800">
                          {emp.employee_name || emp.username}
                        </div>
                        <div className="text-xs text-slate-500">
                          {emp.incidents_count} incident{emp.incidents_count !== 1 ? 'e' : ''} activ{emp.incidents_count !== 1 ? 'e' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-orange-600">
                        Pierdere generată: {emp.total_restant?.toLocaleString()} lei
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded content - incidents table */}
                  {expandedEmployees[emp.employee_id] && (
                    <div className="p-3 bg-white overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[90px]">Data</TableHead>
                            <TableHead className="w-[100px]">AWB</TableHead>
                            <TableHead className="w-[130px]">Tip Incident</TableHead>
                            <TableHead className="w-[80px]">Valoare</TableHead>
                            <TableHead className="min-w-[200px]">Descriere</TableHead>
                            <TableHead className="w-[110px]">Status</TableHead>
                            {canSeePlata && <TableHead className="w-[180px]">Plată</TableHead>}
                            <TableHead className="w-[80px]">Acțiuni</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {emp.incidents.map((inc) => {
                            const statusInfo = getStatusLabel(inc.status, canSeePlata);
                            const StatusIcon = statusInfo.icon;
                            const canFinalize = inc.status === 'in_plata' && inc.suma_restanta <= 0;
                            
                            return (
                              <TableRow key={inc.id}>
                                <TableCell className="text-xs">
                                  {new Date(inc.data_incident).toLocaleDateString('ro-RO')}
                                </TableCell>
                                <TableCell className="text-xs font-mono">
                                  {inc.awb || '-'}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {inc.tip_incident}
                                </TableCell>
                                <TableCell className="text-xs font-semibold text-orange-700">
                                  {inc.valoare?.toLocaleString()} lei
                                </TableCell>
                                <TableCell className="text-xs">
                                  {inc.descriere ? (
                                    <div className="max-w-[250px]">
                                      <p className="truncate">{inc.descriere}</p>
                                      {inc.descriere.length > 50 && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setDescriereDialog({
                                              open: true,
                                              text: inc.descriere,
                                              title: `${inc.tip_incident} - ${inc.awb || 'N/A'}`
                                            });
                                          }}
                                          className="text-blue-600 hover:underline flex items-center gap-1 mt-1"
                                        >
                                          <Eye className="w-3 h-3" />
                                          Vezi tot
                                        </button>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                    <StatusIcon className="w-3 h-3" />
                                    {statusInfo.label}
                                  </span>
                                </TableCell>
                                {canSeePlata && (
                                  <TableCell>
                                    {inc.status === 'in_analiza' ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setPlanRate({
                                            numar_rate: 1,
                                            luna_start: selectedMonth,
                                            an_start: selectedYear
                                          });
                                          setPlataDialog({ open: true, incident: inc });
                                        }}
                                        className="text-xs"
                                      >
                                        <CreditCard className="w-3 h-3 mr-1" />
                                        Pune în plată
                                      </Button>
                                    ) : inc.status === 'in_plata' ? (
                                      <div className="text-xs space-y-1">
                                        <div className="font-semibold">
                                          Total: {inc.valoare?.toLocaleString()} lei
                                        </div>
                                        <div className="text-green-600">
                                          Plătit: {inc.suma_platita?.toLocaleString()} lei
                                        </div>
                                        <div className="text-orange-600">
                                          Rest: {inc.suma_restanta?.toLocaleString()} lei
                                        </div>
                                        {inc.rata_info && (
                                          <div className="text-slate-500">
                                            Rata {inc.rata_info.rate_platite_count}/{inc.rata_info.numar_rate} - {inc.rata_info.rata_lunara} lei/lună
                                          </div>
                                        )}
                                        {inc.rata_info?.are_rata_de_platit && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMarcheazaRata(inc);
                                            }}
                                            className="text-xs mt-1 w-full border-green-300 text-green-700 hover:bg-green-50"
                                          >
                                            <Check className="w-3 h-3 mr-1" />
                                            Marchează rata {MONTHS_RO[selectedMonth]}
                                          </Button>
                                        )}
                                        {canFinalize && (
                                          <Button
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleFinalizeaza(inc);
                                            }}
                                            className="text-xs mt-1 w-full bg-green-600 hover:bg-green-700"
                                          >
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            Finalizează
                                          </Button>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-green-600 font-medium">
                                        ✓ Reconciliat
                                      </span>
                                    )}
                                  </TableCell>
                                )}
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    {inc.status !== 'finalizat' && canEdit && (
                                      <>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openEditDialog(inc);
                                          }}
                                          className="h-7 w-7 p-0"
                                          title="Editează"
                                        >
                                          <Edit className="w-3 h-3" />
                                        </Button>
                                        {canSeePlata && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDelete(inc.id);
                                            }}
                                            className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                            title="Șterge"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        )}
                                      </>
                                    )}
                                    {canSeePlata && inc.status === 'in_plata' && inc.suma_restanta > 0 && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleFinalizeaza(inc);
                                        }}
                                        title="Finalizare manuală"
                                        className="h-7 w-7 p-0 text-green-600 hover:text-green-700"
                                      >
                                        <Archive className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Dialog Adaugă Incident */}
      <Dialog open={addDialog.open} onOpenChange={(open) => setAddDialog({ open })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adaugă Incident Nou</DialogTitle>
            <DialogDescription>
              Înregistrează un nou incident pentru un angajat
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Angajat *</Label>
              <Select 
                value={newIncident.employee_id} 
                onValueChange={(v) => setNewIncident({ ...newIncident, employee_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează angajatul" />
                </SelectTrigger>
                <SelectContent>
                  {employeesList?.filter(d => d.role !== 'admin' && isActiveEmployee(d)).map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.name || (driver.nume && driver.prenume 
                        ? `${driver.nume} ${driver.prenume}`
                        : driver.username)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data Incident *</Label>
                <Input
                  type="date"
                  value={newIncident.data_incident}
                  onChange={(e) => setNewIncident({ ...newIncident, data_incident: e.target.value })}
                />
              </div>
              <div>
                <Label>AWB</Label>
                <Input
                  placeholder="Ex: AWB123456"
                  value={newIncident.awb}
                  onChange={(e) => setNewIncident({ ...newIncident, awb: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tip Incident *</Label>
                <Input
                  placeholder="Ex: Colet pierdut"
                  value={newIncident.tip_incident}
                  onChange={(e) => setNewIncident({ ...newIncident, tip_incident: e.target.value })}
                />
              </div>
              <div>
                <Label>Valoare (lei) *</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={newIncident.valoare}
                  onChange={(e) => setNewIncident({ ...newIncident, valoare: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Descriere Incident</Label>
              <Textarea
                placeholder="Descriere detaliată a incidentului..."
                value={newIncident.descriere}
                onChange={(e) => setNewIncident({ ...newIncident, descriere: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog({ open: false })}>
              Anulează
            </Button>
            <Button onClick={handleAddIncident}>
              Adaugă Incident
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Editare Incident */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, incident: open ? editDialog.incident : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editează Incident</DialogTitle>
            <DialogDescription>
              Modifică datele incidentului
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>AWB</Label>
                <Input
                  placeholder="Ex: AWB123456"
                  value={editIncident.awb}
                  onChange={(e) => setEditIncident({ ...editIncident, awb: e.target.value })}
                />
              </div>
              <div>
                <Label>Tip Incident *</Label>
                <Input
                  placeholder="Ex: Colet pierdut"
                  value={editIncident.tip_incident}
                  onChange={(e) => setEditIncident({ ...editIncident, tip_incident: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Valoare (lei) *</Label>
              <Input
                type="number"
                placeholder="0"
                value={editIncident.valoare}
                onChange={(e) => setEditIncident({ ...editIncident, valoare: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Descriere Incident</Label>
              <Textarea
                placeholder="Descriere detaliată a incidentului..."
                value={editIncident.descriere}
                onChange={(e) => setEditIncident({ ...editIncident, descriere: e.target.value })}
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, incident: null })}>
              Anulează
            </Button>
            <Button onClick={handleSaveEdit}>
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Pune în Plată */}
      <Dialog open={plataDialog.open} onOpenChange={(open) => setPlataDialog({ open, incident: open ? plataDialog.incident : null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pune în Plată</DialogTitle>
            <DialogDescription>
              Configurează planul de rate pentru incidentul de {plataDialog.incident?.valoare?.toLocaleString()} lei
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
              <div className="text-sm font-medium text-orange-800">
                {plataDialog.incident?.tip_incident}
              </div>
              <div className="text-xs text-orange-600 mt-1">
                AWB: {plataDialog.incident?.awb || 'N/A'} | 
                Valoare: {plataDialog.incident?.valoare?.toLocaleString()} lei
              </div>
            </div>
            
            <div>
              <Label>Număr de Rate</Label>
              <Select 
                value={String(planRate.numar_rate)} 
                onValueChange={(v) => setPlanRate({ ...planRate, numar_rate: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                    <SelectItem key={n} value={String(n)}>
                      {n} {n === 1 ? 'rată' : 'rate'} 
                      ({Math.round((plataDialog.incident?.valoare || 0) / n)} lei/lună)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Luna Start</Label>
                <Select 
                  value={String(planRate.luna_start)} 
                  onValueChange={(v) => setPlanRate({ ...planRate, luna_start: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS_RO.slice(1).map((month, idx) => (
                      <SelectItem key={idx + 1} value={String(idx + 1)}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>An Start</Label>
                <Select 
                  value={String(planRate.an_start)} 
                  onValueChange={(v) => setPlanRate({ ...planRate, an_start: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="text-sm font-medium text-blue-800">
                Rezumat Plan Rate
              </div>
              <div className="text-xs text-blue-600 mt-1">
                {planRate.numar_rate} {planRate.numar_rate === 1 ? 'rată' : 'rate'} x {Math.round((plataDialog.incident?.valoare || 0) / planRate.numar_rate)} lei
                <br />
                Începând din {MONTHS_RO[planRate.luna_start]} {planRate.an_start}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlataDialog({ open: false, incident: null })}>
              Anulează
            </Button>
            <Button onClick={handlePuneInPlata}>
              Confirmă Plan Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Dialog Descriere */}
      <Dialog open={descriereDialog.open} onOpenChange={(open) => setDescriereDialog({ ...descriereDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{descriereDialog.title}</DialogTitle>
          </DialogHeader>
          <div className="bg-slate-50 p-4 rounded-lg border max-h-[300px] overflow-y-auto">
            <p className="text-sm whitespace-pre-wrap">{descriereDialog.text}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setDescriereDialog({ open: false, text: '', title: '' })}>
              Închide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default MonitorizareDiferenteTab;
